import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './OurKitchens.css';
// June 2026 layout restored (Jeffrey, 2026-09-11) — the filters, the map, the
// expanding cards and the detail view are Sarb's 4 June page, unchanged.
// What is NOT restored is its copy of the kitchen data: that lived inline here
// and still carried the retired partner name for Kitsilano, along with four
// images that no longer exist. The page now
// reads the single dataset (CMSY-28) so the names, photos and station counts
// are the approved ones, and Apply goes to OUR form with the tracking intact
// rather than to v2's.
import { Link } from 'react-router-dom';
import { kitchens as kitchenData, offsiteFormFor, v2FacilityIdFor } from '../data/kitchens';
import type { Kitchen } from '../data/kitchens';
import { facilitySlugForKitchen } from '../data/facilityPages';
import SEO from '../components/SEO';
import ApplyLink from '../components/ApplyLink';
import { APPLY_FORM_URL, withAttribution, fireApplyHandoffConversion } from '../lib/attribution';

// Custom favicon marker for map
const createCustomIcon = (status: 'operating' | 'coming-soon') => {
  return L.divIcon({
    html: `<img src="/cropped-favicon_og.png" alt="Commissary Connect" class="favicon-marker ${status}" />`,
    className: 'favicon-marker-container',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};


// ImageCarousel Component
interface ImageCarouselProps {
  images: string[];
  kitchenName: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ images, kitchenName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const autoAdvanceRef = useRef<NodeJS.Timeout | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-advance functionality
  useEffect(() => {
    if (images.length <= 1 || isHovered) return;

    autoAdvanceRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
      }
    };
  }, [images.length, isHovered]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Touch handlers for mobile swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Single image case
  if (images.length <= 1) {
    return (
      <div className="kitchen-image-section">
        <img src={images[0]} alt={kitchenName} />
      </div>
    );
  }

  return (
    <div 
      className="kitchen-image-section carousel-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="carousel-images">
        <img 
          src={images[currentIndex]} 
          alt={`${kitchenName} - ${currentIndex + 1}`}
          className="carousel-image"
        />
      </div>
      
      {/* Navigation Arrows */}
      <button 
        className="carousel-arrow carousel-arrow-left" 
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        &#8249;
      </button>
      <button 
        className="carousel-arrow carousel-arrow-right" 
        onClick={goToNext}
        aria-label="Next image"
      >
        &#8250;
      </button>

      {/* Dots Indicator */}
      <div className="carousel-dots">
        {images.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const OurKitchens: React.FC = () => {
  const [selectedKitchen, setSelectedKitchen] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [equipmentFilter, setEquipmentFilter] = useState<string>('all');
  const [businessTypeFilter, setBusinessTypeFilter] = useState<string>('all');
  const [expandedKitchens, setExpandedKitchens] = useState<Set<string>>(new Set());
  const mapRef = useRef<L.Map | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle kitchen expansion
  const toggleKitchenExpansion = (kitchenId: string) => {
    setExpandedKitchens(prev => {
      const newSet = new Set(prev);
      if (newSet.has(kitchenId)) {
        newSet.delete(kitchenId);
      } else {
        newSet.add(kitchenId);
      }
      return newSet;
    });
  };

  // City coordinates for map zooming
  const cityCoordinates: Record<string, { center: [number, number], zoom: number }> = {
    'Vancouver': { center: [49.2454, -123.1095], zoom: 11 },
    'Abbotsford': { center: [49.0504, -122.3045], zoom: 13 },
    'Surrey': { center: [49.1044, -122.8011], zoom: 13 },
    'Langley': { center: [49.115118, -122.657801], zoom: 13 },
    'all': { center: [49.2827, -123.1207], zoom: 10 }
  };

  // Dynamic kitchen data - easily expandable
  const kitchens: Kitchen[] = kitchenData;

  // Filter kitchens based on selected filters
  const filteredKitchens = kitchens.filter(kitchen => {
    const cityMatch = cityFilter === 'all' || kitchen.city === cityFilter;
    const equipmentMatch = equipmentFilter === 'all' || kitchen.kitchenEquipment.some(eq => eq.name === equipmentFilter);
    const businessTypeMatch = businessTypeFilter === 'all' || kitchen.idealFor.includes(businessTypeFilter);
    return cityMatch && equipmentMatch && businessTypeMatch;
  });

  // Get unique values for filter dropdowns
  const cities = Array.from(new Set(kitchens.map(k => k.city))).sort();
  const equipment = Array.from(new Set(kitchens.flatMap(k => k.kitchenEquipment.map(eq => eq.name)))).sort();
  const businessTypes = Array.from(new Set(kitchens.flatMap(k => k.idealFor))).sort();

  // Handle city filter change with map zoom
  const handleCityFilterChange = (city: string) => {
    setCityFilter(city);
    
    // Zoom map to selected city
    if (mapRef.current && cityCoordinates[city]) {
      const { center, zoom } = cityCoordinates[city];
      mapRef.current.setView(center, zoom, { animate: true });
    }
  };

  // Calculate map bounds to restrict zooming
  const getMapBounds = () => {
    if (filteredKitchens.length === 0) return undefined;
    
    const lats = filteredKitchens.map(k => k.coordinates[0]);
    const lngs = filteredKitchens.map(k => k.coordinates[1]);
    
    // Headroom, not decoration. maxBounds is a hard constraint: Leaflet springs the map
    // back inside it, so whatever slack is left here is the only room autoPan has to
    // reveal a popup opening near an edge. At 0.1° there was none, and a popup on the
    // northernmost marker opened clipped and could not be dragged into view.
    // Extra at the top because popups open ABOVE their marker.
    const minLat = Math.min(...lats) - 0.15;
    const maxLat = Math.max(...lats) + 0.45;
    const minLng = Math.min(...lngs) - 0.25;
    const maxLng = Math.max(...lngs) + 0.25;

    return [[minLat, minLng], [maxLat, maxLng]] as L.LatLngBoundsExpression;
  };

  const handleMarkerClick = (kitchenId: string) => {
    setSelectedKitchen(selectedKitchen === kitchenId ? null : kitchenId);
    // Scroll to the kitchen listing with some padding above
    setTimeout(() => {
      const element = document.getElementById(`kitchen-${kitchenId}`);
      if (element) {
        const offsetTop = element.offsetTop - 100; // 100px padding above the tile
        window.scrollTo({ 
          top: offsetTop, 
          behavior: 'smooth' 
        });
      }
    }, 100);
  };

  const handleSelectKitchen = (kitchenId: string) => {
    // Which kitchen maps to which v2 facility id — and which kitchens apply on
    // their own site — is defined once in data/kitchens.ts (CMSY-28). The June
    // version of this handler hard-coded the ids in an if/else whose
    // fall-through silently preselected Laurel HACCP for anything it did not
    // recognise, and sent everyone to v2's form instead of ours.
    const offsite = offsiteFormFor(kitchenId);
    if (offsite) {
      window.gtag?.('event', 'click', {
        event_category: 'Application',
        event_label: `Select Kitchen - ${kitchenId}`,
        value: 1,
      });
      window.open(withAttribution(offsite.siteHandoffHref), '_blank');
      return;
    }
    const facilityId = v2FacilityIdFor(kitchenId);
    fireApplyHandoffConversion(`select_kitchen_${kitchenId}`);
    const dest = withAttribution(facilityId ? `${APPLY_FORM_URL}?facilities=${facilityId}` : APPLY_FORM_URL);
    setTimeout(() => window.location.assign(dest), 250);
  };

  return (
    <div className="kitchens-page">
      {/* Search title and description are ranking copy (CMSY-32) and are invisible
          to a visitor, so they change only when they stop being TRUE. They said
          "6" while the page rendered eight cards; corrected to eight on
          2026-09-16 (Jeffrey: "put the number that we actually have"). The
          geography moved with it: five kitchens are in Vancouver and Surrey and
          Langley are Metro Vancouver, but the Fraser Valley Food Hub is in
          ABBOTSFORD, which is the Fraser Valley Regional District — so "8 Metro
          Vancouver kitchens" would have swapped one wrong number for one wrong
          place. Count and geography are both checked against
          data/kitchens.ts, which is what renders the cards. "Lower Mainland"
          carries both regions inside the 60-character title budget that
          seo-budgets.test.ts enforces; the description names them explicitly.
          The query phrase stays at the front of the title, per CMSY-32. */}
      <SEO
        title="Shared Kitchen Space Near You | 8 Lower Mainland Kitchens"
        description="Shared commercial kitchen space near you: compare eight licensed kitchens across Metro Vancouver and the Fraser Valley by equipment, location and hours."
        keywords="commissary kitchen near me, commercial kitchen near me, shared kitchen near me, kitchen for rent near me"
      />
      {/* One page-level Apply, no facility preselected — the form asks which.
          Kept from the current site: the June page had no action at all until a
          visitor scrolled into a card and committed to a kitchen. */}
      {/* Inside .container like every other block on this page. Without it the
          CTA sat flush at x=0 against the window edge while the filters below
          it were indented — visible on the live site until 2026-09-15. */}
      <div className="container">
        <div className="kitchens-page-cta">
          <ApplyLink className="kitchens-page-apply" label="our_kitchens_page_apply">Apply for kitchen space</ApplyLink>
          <span className="kitchens-page-cta-note">Not sure which kitchen? Apply and we'll help you choose.</span>
        </div>
      </div>
      {/* Filters */}
      <div className="filters-section">
        <div className="container">
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="city-filter">Location:</label>
              <select 
                id="city-filter"
                value={cityFilter} 
                onChange={(e) => handleCityFilterChange(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="equipment-filter">Equipment:</label>
              <select 
                id="equipment-filter"
                value={equipmentFilter} 
                onChange={(e) => setEquipmentFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Equipment</option>
                {equipment.map(eq => (
                  <option key={eq} value={eq}>{eq}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="business-type-filter">Business Type:</label>
              <select 
                id="business-type-filter"
                value={businessTypeFilter} 
                onChange={(e) => setBusinessTypeFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Types</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="results-count">
              {filteredKitchens.length} of {kitchens.length} kitchens
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="map-container">
        <MapContainer
          center={[49.2827, -123.1207]}
          zoom={10}
          style={{ height: '500px', width: '100%' }}
          maxBounds={getMapBounds()}
          maxZoom={13}
          minZoom={9}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          touchZoom={true}
          ref={mapRef}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {filteredKitchens.map((kitchen) => (
            <Marker
              key={kitchen.id}
              position={kitchen.coordinates}
              icon={createCustomIcon(kitchen.status)}
              eventHandlers={{
                click: () => handleMarkerClick(kitchen.id),
                mouseover: (e) => {
                  setTimeout(() => {
                    if (e.target.isPopupOpen && !e.target.isPopupOpen()) {
                      e.target.openPopup();
                    }
                  }, 200);
                },
                mouseout: (e) => {
                  setTimeout(() => {
                    if (e.target.isPopupOpen && e.target.isPopupOpen()) {
                      e.target.closePopup();
                    }
                  }, 300);
                }
              }}
            >
              {/* A popup near the top of the map used to open clipped and stay clipped
                  (Jeffrey, 2026-09-21). Three settings were fighting:

                    autoPan={false}   — the one thing that slides the map so an
                                        edge popup becomes fully visible, switched off.
                    keepInView={true} — pulls the popup back toward the centre, which
                                        is the "it recenters itself" part.
                    maxBounds padded 0.1° (see getMapBounds) — even with autoPan on,
                                        panning up to reveal a popup left the allowed
                                        area and Leaflet sprang the map straight back.

                  Fixing only the popup props would not have worked; the bounds had to
                  give it room.

                  autoPanPadding is the margin kept between the popup and the container
                  EDGE — it is not room reserved for the popup itself, and mistaking it
                  for that is easy: a first attempt here used 220px at the top, which is
                  harmless in the 500px desktop map and impossible in the 300px one
                  mobile uses (OurKitchens.css @768px), where a 223px popup then had 56px
                  to live in and hung off the bottom instead of the top. Measured, not
                  guessed: 223px popup, 300px map, so 24px a side leaves room. */}
              <Popup
                closeButton={false}
                autoClose={false}
                closeOnClick={false}
                autoPan={true}
                autoPanPadding={[24, 24]}
                offset={[0, -40]}
              >
                <div className="map-popup">
                  <h3>{kitchen.name}</h3>
                  <p className="popup-address">{kitchen.address}</p>
                  <p className="popup-size">{kitchen.size} • {kitchen.capacity}</p>
                  <div className="popup-ideal">
                    <strong>Ideal For:</strong> {kitchen.idealFor.join(', ')}
                  </div>
                  <div className="popup-status">
                    <span className={`status-badge ${kitchen.status}`}>
                      {kitchen.status === 'operating' ? 'Operating' : 'Coming Soon'}
                    </span>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Kitchen Listings */}
      <div className="kitchens-listings">
        <div className="container">
          <div className="kitchens-grid">
            {filteredKitchens.map((kitchen) => (
              <div 
                key={kitchen.id} 
                id={`kitchen-${kitchen.id}`}
                className={`kitchen-card ${selectedKitchen === kitchen.id ? 'expanded' : ''} ${kitchen.status}`}
              >
                <div className="commissary-logo">
                  <img 
                    src={kitchen.partnerLogo || "/Commissary-Connect-1.png"} 
                    alt={kitchen.partnerLogo ? "Partner Logo" : "Commissary Connect"} 
                  />
                </div>

                {/* Streamlined Main Section */}
                <div className="kitchen-main-row">
                  {kitchen.images && kitchen.images.length > 0 && (
                    <div className="kitchen-image-section">
                      <ImageCarousel 
                        images={kitchen.images} 
                        kitchenName={kitchen.name}
                      />
                    </div>
                  )}
                  
                  <div className="kitchen-content-section">
                    {/* Address first, name second (CEO, 2026-09-21). The heading is the
                        street AND city — these eight span four municipalities, so a street
                        on its own does not tell anyone how far away it is. The old
                        `subtitle` is no longer rendered here: it meant something different
                        on almost every kitchen (three times just the city, once the address
                        itself, once a real descriptor), so what survives of it is folded
                        into `cardName` per kitchen. The separate address line is gone
                        because the heading is now the address. */}
                    <div className="kitchen-title-block">
                      <h2>{kitchen.cardHeading ?? kitchen.address}</h2>
                      <h3 className="kitchen-subtitle">{kitchen.cardName ?? kitchen.name}</h3>
                      <p className="kitchen-size-capacity">{kitchen.size} • {kitchen.capacity}</p>
                      <span className={`status-badge ${kitchen.status}`}>
                        {kitchen.status === 'operating' ? 'Available Now' : 'Coming Soon'}
                      </span>
                    </div>
                    
                    {/* Key Advantage - More Prominent */}
                    {kitchen.keyAdvantage && (
                      <div className="key-advantage-prominent">
                        {kitchen.keyAdvantage}
                      </div>
                    )}
                    
                    {/* Equipment Summary Tags */}
                    <div className="equipment-summary">
                      <h4>Kitchen Equipment:</h4>
                      <div className="equipment-tags">
                        {kitchen.kitchenEquipment.map((equipment, idx) => (
                          <span key={idx} className="equipment-tag">{equipment.name}</span>
                        ))}
                      </div>
                    </div>

                    {/* Business Types */}
                    <div className="business-types-summary">
                      <h4>Ideal For:</h4>
                      <div className="info-tags">
                        {kitchen.idealFor.map((item, idx) => (
                          <span key={idx} className="info-tag">{item}</span>
                        ))}
                      </div>
                    </div>

                    {/* Short Description */}
                    <div className="kitchen-description-short">
                      <p>
                        {(() => {
                          const firstParagraph = kitchen.description.split('\n\n')[0];
                          const sentences = firstParagraph.split('. ');
                          const shortVersion = sentences.slice(0, 2).join('. ');
                          return shortVersion + (sentences.length > 2 ? '...' : '');
                        })()}
                        {(kitchen.description.split('\n\n').length > 1 || kitchen.description.split('. ').length > 2) && (
                          <button 
                            className="see-more-btn"
                            onClick={() => toggleKitchenExpansion(kitchen.id)}
                          >
                            See more
                          </button>
                        )}
                      </p>
                    </div>

                    {/* Show Details Button */}
                    <button 
                      className="show-details-btn"
                      onClick={() => toggleKitchenExpansion(kitchen.id)}
                    >
                      {expandedKitchens.has(kitchen.id) ? 'Hide Details ▲' : 'Show Details ▼'}
                    </button>
                  </div>
                </div>

                {/* Expandable Detailed Section */}
                {expandedKitchens.has(kitchen.id) && (
                  <div className="kitchen-details-expanded">
                    {/* Full Description - excluding the short version already shown */}
                    <div className="full-description">
                      <h4>Complete Details</h4>
                      <div className="kitchen-description">
                        {(() => {
                          const paragraphs = kitchen.description.split('\n\n');
                          const firstParagraph = paragraphs[0];
                          const sentences = firstParagraph.split('. ');
                          
                          // If there are more than 2 sentences in first paragraph, show the remaining sentences
                          const remainingFirstParagraph = sentences.length > 2 
                            ? sentences.slice(2).join('. ') + (sentences.slice(2).length > 0 ? '.' : '')
                            : '';
                          
                          // Show remaining sentences from first paragraph if any
                          return (
                            <>
                              {remainingFirstParagraph && <p>{remainingFirstParagraph}</p>}
                              {/* Show all subsequent paragraphs */}
                              {paragraphs.slice(1).map((paragraph, idx) => (
                                <p key={idx + 1}>{paragraph}</p>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Services */}
                    <div className="detail-section">
                      <h4>Included Services & Features</h4>
                      <div className="icon-grid">
                        {kitchen.includedServices.map((service, idx) => (
                          <div key={idx} className="icon-item">
                            <img src={service.icon} alt={service.name} />
                            <span>{service.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Certifications */}
                    <div className="detail-section">
                      <h4>Certifications & Compliance</h4>
                      <div className="tags">
                        {kitchen.certifications.map((cert, idx) => (
                          <span key={idx} className="tag certification">{cert}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="kitchen-action-section">
                  <button
                    className="select-kitchen-btn"
                    onClick={() => handleSelectKitchen(kitchen.id)}
                    disabled={kitchen.status === 'coming-soon'}
                  >
                    {kitchen.status === 'operating' ? 'Apply for This Kitchen' : 'Notify When Available'}
                  </button>
                  {/* The only internal link to a facility landing page since the
                      homepage tiles stopped rendering on 2026-09-11 — those six
                      pages were reachable from ads and search but from nowhere
                      inside the site (CMSY-75). Shown on the collapsed card on
                      purpose: inside the expanded details nobody would find it.
                      Kitchens with no landing page get nothing; never guess a
                      slug, it would point the card at another kitchen. */}
                  {(() => {
                    const slug = facilitySlugForKitchen(kitchen.id);
                    if (!slug) return null;
                    return (
                      <Link className="kitchen-facility-link" to={`/kitchens/${slug}`}>
                        See the facility
                      </Link>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurKitchens;